import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateSelectionAccuracy,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./SemanticClassification.css";

const SEMANTIC_CATEGORIES = [
  {
    id: "transport",
    title: "Medios de transporte",
    correct: [
      "Coche",
      "Avión",
      "Bicicleta",
      "Barco",
      "Autobús",
      "Tren",
      "Motocicleta",
    ],
    distractors: [
      "Pelota",
      "Zumo",
      "Sartén",
      "Armario",
      "Perro",
      "Lápiz",
      "Galleta",
    ],
  },
  {
    id: "four_legged_animals",
    title: "Animales con cuatro patas",
    correct: [
      "Perro",
      "Gato",
      "León",
      "Caballo",
      "Vaca",
      "Elefante",
      "Conejo",
    ],
    distractors: [
      "Águila",
      "Serpiente",
      "Flamenco",
      "Mariposa",
      "Pez",
      "Pulpo",
      "Mosca",
    ],
  },
  {
    id: "kitchen",
    title: "Cosas que puedes encontrar en una cocina",
    correct: [
      "Cuchara",
      "Vaso",
      "Cuchillo",
      "Sartén",
      "Plato",
      "Horno",
      "Nevera",
    ],
    distractors: [
      "Maleta",
      "Sombrilla",
      "Almohada",
      "Semáforo",
      "Balón",
      "Mochila",
      "Columpio",
    ],
  },
  {
    id: "green_food",
    title: "Alimentos de color verde",
    correct: [
      "Lechuga",
      "Pera",
      "Brócoli",
      "Pepino",
      "Aguacate",
      "Espinacas",
      "Kiwi",
    ],
    distractors: [
      "Huevo",
      "Zanahoria",
      "Fresa",
      "Naranja",
      "Tomate",
      "Arroz",
      "Chocolate",
    ],
  },
  {
    id: "furniture",
    title: "Muebles",
    correct: [
      "Sofá",
      "Armario",
      "Mesa",
      "Cama",
      "Silla",
      "Estantería",
      "Escritorio",
    ],
    distractors: [
      "Lapicero",
      "Salón",
      "Ventana",
      "Jarrón",
      "Cuchara",
      "Camiseta",
      "Guitarra",
    ],
  },
  {
    id: "flying_animals",
    title: "Animales que vuelan",
    correct: [
      "Pelícano",
      "Mariposa",
      "Cigüeña",
      "Murciélago",
      "Águila",
      "Mosca",
      "Paloma",
    ],
    distractors: [
      "Elefante",
      "Vaca",
      "Caballo",
      "Serpiente",
      "Perro",
      "Tortuga",
      "Delfín",
    ],
  },
  {
    id: "liquids",
    title: "Líquidos",
    correct: [
      "Agua",
      "Leche",
      "Zumo",
      "Aceite",
      "Sopa",
      "Refresco",
      "Vinagre",
    ],
    distractors: [
      "Manzana",
      "Magdalena",
      "Galleta",
      "Pan",
      "Arroz",
      "Queso",
      "Chocolate",
    ],
  },
  {
    id: "flowers",
    title: "Nombres de flores",
    correct: [
      "Rosa",
      "Margarita",
      "Jazmín",
      "Tulipán",
      "Orquídea",
      "Clavel",
      "Lirio",
    ],
    distractors: [
      "Limón",
      "Berta",
      "María",
      "Roble",
      "Manzana",
      "Césped",
      "Pino",
    ],
  },
  {
    id: "clothes",
    title: "Prendas de ropa",
    correct: [
      "Falda",
      "Chaqueta",
      "Camisa",
      "Pantalón",
      "Camiseta",
      "Abrigo",
      "Vestido",
    ],
    distractors: [
      "Armario",
      "Parchís",
      "Motocicleta",
      "Zapatero",
      "Percha",
      "Maleta",
      "Espejo",
    ],
  },
  {
    id: "sitting",
    title: "Objetos en los que puedes sentarte",
    correct: [
      "Banco",
      "Banqueta",
      "Silla",
      "Sofá",
      "Taburete",
      "Sillón",
      "Butaca",
    ],
    distractors: [
      "Maceta",
      "Sandía",
      "Globo",
      "Mesa",
      "Armario",
      "Alfombra",
      "Nevera",
    ],
  },
  {
    id: "school_material",
    title: "Material escolar",
    correct: [
      "Lápiz",
      "Cuaderno",
      "Bolígrafo",
      "Goma",
      "Regla",
      "Estuche",
      "Mochila",
    ],
    distractors: [
      "Sartén",
      "Almohada",
      "Tenedor",
      "Semáforo",
      "Cepillo",
      "Cuchillo",
      "Jarrón",
    ],
  },
  {
    id: "fruit",
    title: "Frutas",
    correct: [
      "Manzana",
      "Plátano",
      "Pera",
      "Sandía",
      "Naranja",
      "Fresa",
      "Melocotón",
    ],
    distractors: [
      "Lechuga",
      "Zanahoria",
      "Patata",
      "Arroz",
      "Pan",
      "Queso",
      "Brócoli",
    ],
  },
  {
    id: "bathroom",
    title: "Cosas que puedes encontrar en un baño",
    correct: [
      "Toalla",
      "Jabón",
      "Espejo",
      "Cepillo de dientes",
      "Champú",
      "Peine",
      "Papel higiénico",
    ],
    distractors: [
      "Sartén",
      "Televisor",
      "Semáforo",
      "Mochila",
      "Martillo",
      "Balón",
      "Ordenador",
    ],
  },
  {
    id: "tools",
    title: "Herramientas",
    correct: [
      "Martillo",
      "Destornillador",
      "Alicates",
      "Sierra",
      "Taladro",
      "Llave inglesa",
      "Cinta métrica",
    ],
    distractors: [
      "Cuchara",
      "Almohada",
      "Zumo",
      "Camiseta",
      "Cuaderno",
      "Pelota",
      "Manzana",
    ],
  },
  {
    id: "round_objects",
    title: "Objetos que suelen ser redondos",
    correct: [
      "Pelota",
      "Moneda",
      "Plato",
      "Rueda",
      "Reloj",
      "Botón",
      "Globo",
    ],
    distractors: [
      "Libro",
      "Puerta",
      "Regla",
      "Mesa",
      "Cuaderno",
      "Lápiz",
      "Cama",
    ],
  },
  {
    id: "box",
    title: "Objetos que pueden caber dentro de una caja",
    correct: [
      "Anillo",
      "Chupete",
      "Dado",
      "Bolígrafo",
      "Llave",
      "Pelota pequeña",
      "Muñeco",
    ],
    distractors: [
      "Caballo",
      "Portería",
      "Autobús",
      "Árbol",
      "Edificio",
      "Elefante",
      "Piscina",
    ],
  },
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 6,
    targetCount: 3,
  },

  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 8,
    targetCount: 4,
  },

  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 10,
    targetCount: 5,
  },
};

const shuffleArray = (items) => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));

    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const createRound = ({
  itemCount,
  targetCount,
  previousCategoryId = null,
}) => {
  const safeItemCount = Math.min(
    Math.max(Number(itemCount) || 6, 4),
    12
  );

  const safeTargetCount = Math.min(
    Math.max(Number(targetCount) || 3, 1),
    safeItemCount - 1
  );

  const availableCategories = SEMANTIC_CATEGORIES.filter(
    (category) =>
      category.id !== previousCategoryId &&
      category.correct.length >= safeTargetCount &&
      category.distractors.length >= safeItemCount - safeTargetCount
  );

  const pool =
    availableCategories.length > 0
      ? availableCategories
      : SEMANTIC_CATEGORIES;

  const category =
    pool[Math.floor(Math.random() * pool.length)];

  const correctWords = shuffleArray(category.correct).slice(
    0,
    safeTargetCount
  );

  const distractorWords = shuffleArray(category.distractors).slice(
    0,
    safeItemCount - safeTargetCount
  );

  const options = shuffleArray([
    ...correctWords.map((word, index) => ({
      id: `${category.id}-correct-${index}-${word}`,
      word,
      isCorrect: true,
    })),

    ...distractorWords.map((word, index) => ({
      id: `${category.id}-distractor-${index}-${word}`,
      word,
      isCorrect: false,
    })),
  ]);

  return {
    id: `${Date.now()}-${Math.random()}`,
    categoryId: category.id,
    title: category.title,
    targetCount: correctWords.length,
    options,
  };
};

function SemanticClassification() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [selectedOptionIds, setSelectedOptionIds] = useState([]);

  const [correctSelections, setCorrectSelections] = useState(0);
  const [wrongSelections, setWrongSelections] = useState(0);
  const [omissions, setOmissions] = useState(0);
  const [perfectRounds, setPerfectRounds] = useState(0);

  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [roundStartTime, setRoundStartTime] = useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () => calculateProgress(roundIndex, totalRounds),
    [roundIndex, totalRounds]
  );

  const accuracy = calculateSelectionAccuracy({
    correctSelections,
    wrongSelections,
    omissions,
  });

  const errors = calculateErrorCount(
    wrongSelections,
    omissions
  );

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (
    difficultyOrConfig = selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: resolvedConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const nextConfig = {
      ...resolvedConfig,

      difficultyKey,

      totalRounds:
        Number(resolvedConfig.totalRounds) || 8,

      itemCount: Math.min(
        Math.max(
          Number(resolvedConfig.itemCount) || 6,
          4
        ),
        12
      ),

      targetCount: Math.max(
        Number(resolvedConfig.targetCount) || 3,
        1
      ),
    };

    nextConfig.targetCount = Math.min(
      nextConfig.targetCount,
      nextConfig.itemCount - 1,
      7
    );

    const firstRound = createRound(nextConfig);
    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(firstRound);

    setStarted(true);
    setFinished(false);

    setSelectedOptionIds([]);

    setCorrectSelections(0);
    setWrongSelections(0);
    setOmissions(0);
    setPerfectRounds(0);

    setResponses([]);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
  };

  const toggleOption = (optionId) => {
    setSelectedOptionIds((current) =>
      current.includes(optionId)
        ? current.filter(
            (selectedId) => selectedId !== optionId
          )
        : [...current, optionId]
    );
  };

  const finishGame = () => {
    setEndTime(Date.now());
    setStarted(false);
    setFinished(true);
  };

  const confirmSelection = () => {
    if (!round || selectedOptionIds.length === 0) {
      return;
    }

    const responseTime =
      Date.now() - roundStartTime;

    const selectedOptions = round.options.filter(
      (option) =>
        selectedOptionIds.includes(option.id)
    );

    const selectedCorrectly = selectedOptions.filter(
      (option) => option.isCorrect
    ).length;

    const selectedIncorrectly = selectedOptions.filter(
      (option) => !option.isCorrect
    ).length;

    const roundOmissions = round.options.filter(
      (option) =>
        option.isCorrect &&
        !selectedOptionIds.includes(option.id)
    ).length;

    const isPerfectRound =
      selectedCorrectly === round.targetCount &&
      selectedIncorrectly === 0 &&
      roundOmissions === 0;

    setCorrectSelections(
      (current) => current + selectedCorrectly
    );

    setWrongSelections(
      (current) => current + selectedIncorrectly
    );

    setOmissions(
      (current) => current + roundOmissions
    );

    if (isPerfectRound) {
      setPerfectRounds(
        (current) => current + 1
      );
    }

    setResponses((current) => [
      ...current,
      responseTime,
    ]);

    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= totalRounds) {
      finishGame();
      return;
    }

    const nextRound = createRound({
      ...config,
      previousCategoryId: round.categoryId,
    });

    setRoundIndex(nextRoundIndex);
    setRound(nextRound);
    setSelectedOptionIds([]);
    setRoundStartTime(Date.now());
  };

  if (!started && !finished) {
    return (
      <GameLauncher
        title="Clasificación semántica"
        description="Selecciona todas las palabras que pertenecen a la categoría indicada."
        areas={[
          "Comprensión del lenguaje",
          "Clasificación semántica",
          "Vocabulario",
          "Atención selectiva",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            min: 4,
            max: 30,
            defaultValue: 10,
          }),

          GameFields.itemCount({
            label: "Palabras mostradas",
            min: 4,
            max: 12,
            defaultValue: 8,
          }),

          GameFields.targetCount({
            label: "Palabras correctas",
            min: 1,
            max: 7,
            defaultValue: 4,
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  if (finished) {
    return (
      <div className="semantic-classification-page">
        <div className="semantic-classification-results">
          <GameResults
            title="Resultados"
            correctAnswers={perfectRounds}
            totalAnswers={totalRounds}
            accuracy={accuracy}
            errors={errors}
            omissions={omissions}
            averageReactionTime={
              averageReactionTime
            }
            totalTime={totalTime}
            onRestart={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          />

          <div className="semantic-classification-results-grid">
            <div className="semantic-classification-result-card">
              <span>Rondas perfectas</span>

              <strong>
                {perfectRounds}/{totalRounds}
              </strong>
            </div>

            <div className="semantic-classification-result-card">
              <span>Selecciones correctas</span>
              <strong>{correctSelections}</strong>
            </div>

            <div className="semantic-classification-result-card">
              <span>Selecciones incorrectas</span>
              <strong>{wrongSelections}</strong>
            </div>

            <div className="semantic-classification-result-card">
              <span>Omisiones</span>
              <strong>{omissions}</strong>
            </div>
          </div>

          <GameInterpretation
            interpretation={interpretation}
          />

          <button
            type="button"
            className="semantic-classification-restart"
            onClick={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          >
            Jugar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="semantic-classification-page">
      <main className="semantic-classification-card">
        <header className="semantic-classification-header">
          <div>
            <p className="semantic-classification-area">
              Comprensión del lenguaje
            </p>

            <h1>Clasificación semántica</h1>

            <p>
              Selecciona todas las palabras que
              pertenecen a la categoría.
            </p>
          </div>

          <div className="semantic-classification-score">
            <span>Rondas perfectas</span>
            <strong>{perfectRounds}</strong>
          </div>
        </header>

        <GameProgress
          currentRound={roundIndex + 1}
          totalRounds={totalRounds}
          progress={progress}
        />

        <section className="semantic-classification-game">
          <div className="semantic-classification-category">
            <span>Categoría</span>
            <h2>{round?.title}</h2>
          </div>

          <p className="semantic-classification-instruction">
            Puede haber varias respuestas correctas.
            Selecciónalas y pulsa confirmar.
          </p>

          <div className="semantic-classification-grid">
            {round?.options.map((option) => {
              const isSelected =
                selectedOptionIds.includes(
                  option.id
                );

              return (
                <button
                  type="button"
                  key={option.id}
                  className={`semantic-classification-option ${
                    isSelected
                      ? "semantic-classification-option-selected"
                      : ""
                  }`}
                  onClick={() =>
                    toggleOption(option.id)
                  }
                  aria-pressed={isSelected}
                >
                  {option.word}
                </button>
              );
            })}
          </div>

          <div className="semantic-classification-selection">
            <span>Palabras seleccionadas</span>
            <strong>{selectedOptionIds.length}</strong>
          </div>

          <button
            type="button"
            className="semantic-classification-confirm"
            onClick={confirmSelection}
            disabled={
              selectedOptionIds.length === 0
            }
          >
            Confirmar selección
          </button>
        </section>
      </main>
    </div>
  );
}

export default SemanticClassification;